import { api } from "../api/Middleware";
import {
  CUSTOMER_PROFILES_LIST_URL,
  CUSTOMER_PROFILES_CREATE_URL,
  CUSTOMER_PROFILES_DETAIL_URL,
  CUSTOMER_PROFILES_UPDATE_URL,
  CUSTOMER_PROFILES_DELETE_URL,
} from "../api/Urls";

// =============================
// CUSTOMER PROFILES SERVICES
// =============================

// Listar perfis de clientes
export const listCustomerProfiles = async (params = {}) => {
  return await api.get(CUSTOMER_PROFILES_LIST_URL, { params });
};

// Buscar detalhes de um perfil específico
export const getCustomerProfile = async (id) => {
  return await api.get(CUSTOMER_PROFILES_DETAIL_URL(id));
};

// Criar novo perfil de cliente
export const createCustomerProfile = async (data) => {
  return await api.post(CUSTOMER_PROFILES_CREATE_URL, data);
};

// Atualizar perfil de cliente
export const updateCustomerProfile = async (id, data) => {
  return await api.put(CUSTOMER_PROFILES_UPDATE_URL(id), data);
};

// Deletar perfil de cliente
export const deleteCustomerProfile = async (id) => {
  return await api.delete(CUSTOMER_PROFILES_DELETE_URL(id));
};

// =============================
// HELPER FUNCTIONS
// =============================

// Construir parâmetros de busca para a API
export const buildSearchParams = (filters) => {
  const params = {};

  // Paginação
  if (filters.page) params.page = filters.page;
  if (filters.page_size) params.page_size = filters.page_size;

  // Busca
  if (filters.search) params.search = filters.search;

  // Filtros
  if (filters.is_active !== undefined) params.is_active = filters.is_active;

  // Ordenação
  if (filters.ordering) params.ordering = filters.ordering;

  return params;
};

// Formatar dados do perfil para exibição
export const formatCustomerProfileData = (profile) => {
  return {
    ...profile,
    // Status formatado
    statusVariant: profile.is_active ? "success" : "secondary",
    statusText: profile.is_active ? "Ativo" : "Inativo",
    
    // Datas formatadas
    formattedCreatedAt: profile.created_at 
      ? new Date(profile.created_at).toLocaleDateString("pt-BR")
      : "—",
    formattedUpdatedAt: profile.updated_at 
      ? new Date(profile.updated_at).toLocaleDateString("pt-BR")
      : "—",
    
    // CPF/CNPJ formatado
    formattedCpfCnpj: formatCpfCnpj(profile.cpf_cnpj),
    
    // Telefone formatado
    formattedPhone: formatPhone(profile.phone),
  };
};

// Formatar CPF/CNPJ
export const formatCpfCnpj = (value) => {
  if (!value) return "—";
  
  // Remove caracteres não numéricos
  const clean = value.replace(/\D/g, "");
  
  if (clean.length === 11) {
    // CPF: 000.000.000-00
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  } else if (clean.length === 14) {
    // CNPJ: 00.000.000/0000-00
    return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  
  return value; // Retorna original se não for CPF nem CNPJ válido
};

// Formatar telefone
export const formatPhone = (value) => {
  if (!value) return "—";
  
  // Remove caracteres não numéricos
  const clean = value.replace(/\D/g, "");
  
  if (clean.length === 11) {
    // Celular: (00) 00000-0000
    return clean.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  } else if (clean.length === 10) {
    // Fixo: (00) 0000-0000
    return clean.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }
  
  return value; // Retorna original se não for formato válido
};

// Validar CPF
export const validateCPF = (cpf) => {
  if (!cpf) return false;
  
  const clean = cpf.replace(/\D/g, "");
  
  if (clean.length !== 11) return false;
  if (/^(\d)\1+$/.test(clean)) return false; // Todos os dígitos iguais
  
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(clean.charAt(i)) * (10 - i);
  }
  let checkDigit = 11 - (sum % 11);
  if (checkDigit === 10 || checkDigit === 11) checkDigit = 0;
  if (checkDigit !== parseInt(clean.charAt(9))) return false;
  
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(clean.charAt(i)) * (11 - i);
  }
  checkDigit = 11 - (sum % 11);
  if (checkDigit === 10 || checkDigit === 11) checkDigit = 0;
  if (checkDigit !== parseInt(clean.charAt(10))) return false;
  
  return true;
};

// Validar CNPJ
export const validateCNPJ = (cnpj) => {
  if (!cnpj) return false;
  
  const clean = cnpj.replace(/\D/g, "");
  
  if (clean.length !== 14) return false;
  if (/^(\d)\1+$/.test(clean)) return false; // Todos os dígitos iguais
  
  let length = clean.length - 2;
  let numbers = clean.substring(0, length);
  const digits = clean.substring(length);
  let sum = 0;
  let pos = length - 7;
  
  for (let i = length; i >= 1; i--) {
    sum += numbers.charAt(length - i) * pos--;
    if (pos < 2) pos = 9;
  }
  
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;
  
  length = length + 1;
  numbers = clean.substring(0, length);
  sum = 0;
  pos = length - 7;
  
  for (let i = length; i >= 1; i--) {
    sum += numbers.charAt(length - i) * pos--;
    if (pos < 2) pos = 9;
  }
  
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1))) return false;
  
  return true;
};

// Validar dados do perfil antes de enviar para API
export const validateCustomerProfileData = (data) => {
  const errors = {};

  if (!data.name || data.name.trim().length < 2) {
    errors.name = "Nome é obrigatório e deve ter pelo menos 2 caracteres";
  }

  if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.email = "Email é obrigatório e deve ser válido";
  }

  if (!data.cpf_cnpj) {
    errors.cpf_cnpj = "CPF ou CNPJ é obrigatório";
  } else {
    const clean = data.cpf_cnpj.replace(/\D/g, "");
    if (clean.length === 11) {
      if (!validateCPF(data.cpf_cnpj)) {
        errors.cpf_cnpj = "CPF inválido";
      }
    } else if (clean.length === 14) {
      if (!validateCNPJ(data.cpf_cnpj)) {
        errors.cpf_cnpj = "CNPJ inválido";
      }
    } else {
      errors.cpf_cnpj = "CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos";
    }
  }

  if (data.phone) {
    const clean = data.phone.replace(/\D/g, "");
    if (clean.length < 10 || clean.length > 11) {
      errors.phone = "Telefone deve ter 10 ou 11 dígitos";
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Verificar se o usuário pode executar uma ação
export const canPerformAction = (action, userContext, profile) => {
  const { user, isOrganization } = userContext;

  // Admin sempre pode tudo
  if (user?.superuser_level === 1) return true;

  // Organização pode gerenciar seus perfis
  if (isOrganization && user?.superuser_level <= 2) return true;

  // Outras verificações específicas por ação
  switch (action) {
    case "view":
      return user?.superuser_level <= 3;
    case "create":
    case "edit":
    case "delete":
      return user?.superuser_level <= 2;
    default:
      return false;
  }
};

export default {
  listCustomerProfiles,
  getCustomerProfile,
  createCustomerProfile,
  updateCustomerProfile,
  deleteCustomerProfile,
  buildSearchParams,
  formatCustomerProfileData,
  formatCpfCnpj,
  formatPhone,
  validateCPF,
  validateCNPJ,
  validateCustomerProfileData,
  canPerformAction,
};